import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";

import Breadcrumb from "../components/Breadcrumb";
import { Button } from "../components/Form/Button";
import { Input } from "../components/Form/Input";
import { FormErrorsSchema } from "../types";
import { mergeErrors } from "../utils";
import notifications from "../services/notifications";
import { useIdentityPrefill } from "../hooks/useIdentityPrefill";
import AuthProvider from "../services/auth/AuthProvider/Index";
import { setUserData } from "../../redux/actions/userDataActions";

const ProfileUpdate = ({ isAccountSetup = false }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [givenName, setGivenName] = useState("");
  const [familyName, setFamilyName] = useState("");
  const [formErrors, setFormErrors] = useState<FormErrorsSchema>({});
  const navigate = useNavigate();

  const handleIdentity = useCallback((identity: any) => {
    setGivenName(identity.given_name);
    setFamilyName(identity.family_name);
  }, []);

  const { loadingIdentity } = useIdentityPrefill(handleIdentity);

  return (
    <div className="w-full bg-gray-100">
      <div className="w-full lg:w-2/3 min-h-screen bg-gray-100 text-gray-900">
        <main className="mx-auto px-4 sm:px-6 lg:px-8 pt-4">
          {!isAccountSetup && (
            <Breadcrumb
              pages={[
                { name: "My account", href: "#", current: false },
                { name: "Profile update", href: "#", current: true },
              ]}
            />
          )}
          <div className="mt-8">
            <h1 className="text-xl font-semibold">Profile update</h1>
          </div>
          <div className="bg-white mt-6 px-6 pb-6 pt-2">
            <div className="w-full">
              <div className="w-full mt-6">
                <Input
                  label="First name"
                  name="given_name"
                  placeholder=""
                  errors={formErrors}
                  value={givenName}
                  setValue={setGivenName}
                  disabled={loading || loadingIdentity}
                  required={true}
                  inputMask={(inputText: string) => {
                    return inputText.replaceAll(/[^a-zA-Z0-9 ]/g, "");
                  }}
                />
              </div>
              <div className="w-full mt-6">
                <Input
                  label="Last name"
                  name="family_name"
                  placeholder=""
                  errors={formErrors}
                  value={familyName}
                  setValue={setFamilyName}
                  disabled={loading || loadingIdentity}
                  required={true}
                  inputMask={(inputText: string) => {
                    return inputText.replaceAll(/[^a-zA-Z0-9 ]/g, "");
                  }}
                />
              </div>
            </div>
            <div className="w-full mt-10">
              <Button
                label="Update profile"
                disabled={loading}
                theme="primary"
                size="medium"
                className="block w-full md:ml-auto md:w-auto"
                onClick={async () => {
                  try {
                    setLoading(true);
                    setFormErrors({});

                    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/v1/private/identity/me`;

                    const response = await fetch(apiUrl, {
                      headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${await AuthProvider.getValidAuthorizationToken()}`,
                      },
                      method: "PATCH",
                      body: JSON.stringify({
                        given_name: givenName,
                        family_name: familyName,
                      }),
                    });

                    if (!response.ok) {
                      if (response.status === 422) {
                        const errorData = await response.json();
                        setFormErrors(mergeErrors(errorData.errors));
                      } else {
                        throw new Error("Failed to load resource");
                      }
                    }

                    if (response.ok) {
                      const identity = await AuthProvider.getIdentity();

                      if (identity) {
                        const dispatchSetUserData = (value: any) => {
                          dispatch(setUserData(value));
                        };
                        dispatchSetUserData(identity);
                        setGivenName(identity.given_name);
                        setFamilyName(identity.family_name);
                      }

                      if (isAccountSetup) {
                        notifications.success.queue("Your profile has been successfully updated.");
                        navigate(0);
                      } else {
                        notifications.success({message: "Your profile has been successfully updated."});
                      }
                    }
                  } catch (error) {
                    console.error("Error fetching data:", error);

                    notifications.error({message: "Something went wrong while trying to update your profile."});
                  } finally {
                    setLoading(false);
                  }
                }}
              />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default ProfileUpdate;
